import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import axios from 'axios';

const STRENGTH_LABELS = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'];

function strengthOf(p) {
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
}

const RegistrationPage = () => {
    const [first, setFirst] = useState('');
    const [last, setLast] = useState('');
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [pw, setPw] = useState('');
    const [pw2, setPw2] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [agree, setAgree] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [country, setCountry] = useState('');
    const [city, setCity] = useState('');
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const navigate = useNavigate();

    const strength = strengthOf(pw);
    const pwMatch = pw && pw2 && pw === pw2;
    const pwMismatch = pw2.length > 0 && pw !== pw2;
    const canSubmit = first && last && username && email.includes('@') && strength >= 2 && pwMatch && agree && country && city;

    useEffect(() => {
        axios.get('https://countriesnow.space/api/v0.1/countries/iso')
            .then(r => setCountries(r.data?.data ?? []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!country) { setCities([]); setCity(''); return; }
        setCitiesLoading(true);
        setCity('');
        axios.post('https://countriesnow.space/api/v0.1/countries/cities', { country })
            .then(r => setCities(r.data?.data ?? []))
            .catch(() => setCities([]))
            .finally(() => setCitiesLoading(false));
    }, [country]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!first || !last) { setError('Please enter your full name.'); return; }
        if (!username) { setError('Please choose a username.'); return; }
        if (!email.includes('@')) { setError('Enter a valid email address.'); return; }
        if (strength < 2) { setError('Choose a stronger password — at least 8 characters with a number.'); return; }
        if (!pwMatch) { setError("Passwords don't match."); return; }
        if (!agree) { setError("You'll need to agree to the terms to continue."); return; }
        if (!country) { setError('Please select your country.'); return; }
        if (!city) { setError('Please select your city.'); return; }

        setBusy(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/register`, {
                username,
                password: pw,
                firstname: first,
                lastname: last,
                email,
                role: 'USER',
                country,
                city,
            });
            navigate('/login', { state: { registered: true } });
        } catch (err) {
            const msg = err.response?.data?.message;
            setError(msg || 'Registration failed. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="lg-page">
            <div className="lg-shell">

                {/* ── Left: form column ── */}
                <div className="lg-form-col">
                    <div className="lg-form-wrap rg-wrap">
                        <header className="lg-form-head">
                            <span className="eyebrow">New account</span>
                            <h1 className="lg-title">Join <span className="lg-serif">Interim.</span></h1>
                            <p className="lg-sub">Takes about a minute. You can finish your profile after.</p>
                        </header>

                        <form className="lg-form" onSubmit={handleSubmit}>

                            {/* Name row */}
                            <div className="rg-row">
                                <label className="lg-field">
                                    <span className="lg-label">First name</span>
                                    <input
                                        type="text"
                                        autoComplete="given-name"
                                        className="lg-input"
                                        placeholder="Billie"
                                        value={first}
                                        onChange={e => setFirst(e.target.value)}
                                        autoFocus
                                    />
                                </label>
                                <label className="lg-field">
                                    <span className="lg-label">Last name</span>
                                    <input
                                        type="text"
                                        autoComplete="family-name"
                                        className="lg-input"
                                        placeholder="Jean"
                                        value={last}
                                        onChange={e => setLast(e.target.value)}
                                    />
                                </label>
                            </div>

                            {/* Username */}
                            <label className="lg-field">
                                <span className="lg-label">Username</span>
                                <input
                                    type="text"
                                    autoComplete="username"
                                    className="lg-input"
                                    placeholder="e.g. billie.j"
                                    value={username}
                                    onChange={e => setUsername(e.target.value.replace(/\s/g, '').toLowerCase())}
                                />
                            </label>

                            {/* Email */}
                            <label className="lg-field">
                                <span className="lg-label">Email</span>
                                <input
                                    type="email"
                                    autoComplete="email"
                                    className="lg-input"
                                    placeholder="billie@example.com"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                />
                            </label>

                            {/* Password + strength */}
                            <label className="lg-field">
                                <span className="lg-field-row">
                                    <span className="lg-label">Password</span>
                                    {pw && (
                                        <span className={`rg-strength-label rg-strength-${strength}`}>
                                            {STRENGTH_LABELS[strength]}
                                        </span>
                                    )}
                                </span>
                                <span className="lg-password">
                                    <input
                                        type={showPw ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        className="lg-input"
                                        placeholder="At least 8 characters"
                                        value={pw}
                                        onChange={e => setPw(e.target.value)}
                                    />
                                    <button type="button" className="lg-show" onClick={() => setShowPw(s => !s)}>
                                        {showPw ? 'Hide' : 'Show'}
                                    </button>
                                </span>
                                {pw && (
                                    <div className="rg-strength-bar">
                                        {[1, 2, 3, 4].map(i => (
                                            <span
                                                key={i}
                                                className={`rg-strength-seg${strength >= i ? ` is-on rg-strength-${strength}` : ''}`}
                                            />
                                        ))}
                                    </div>
                                )}
                            </label>

                            {/* Confirm password */}
                            <label className="lg-field">
                                <span className="lg-field-row">
                                    <span className="lg-label">Confirm password</span>
                                    {pwMatch && (
                                        <span className="rg-strength-label rg-strength-3">
                                            <Check size={11} /> Matches
                                        </span>
                                    )}
                                    {pwMismatch && (
                                        <span className="rg-strength-label rg-strength-1">Doesn't match</span>
                                    )}
                                </span>
                                <input
                                    type={showPw ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    className={`lg-input${pwMismatch ? ' rg-input-error' : ''}`}
                                    placeholder="Re-enter password"
                                    value={pw2}
                                    onChange={e => setPw2(e.target.value)}
                                />
                            </label>

                            {/* Location */}
                            <div className="rg-row">
                                <label className="lg-field">
                                    <span className="lg-label">Country</span>
                                    <select
                                        className="lg-input"
                                        value={country}
                                        onChange={e => setCountry(e.target.value)}
                                    >
                                        <option value="">Select country…</option>
                                        {countries.map(c => (
                                            <option key={c.Iso2} value={c.name}>{c.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="lg-field">
                                    <span className="lg-label">City</span>
                                    <select
                                        className="lg-input"
                                        value={city}
                                        onChange={e => setCity(e.target.value)}
                                        disabled={!country || citiesLoading}
                                    >
                                        <option value="">
                                            {!country ? 'Select country first' : citiesLoading ? 'Loading…' : 'Select city…'}
                                        </option>
                                        {cities.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            {/* Terms */}
                            <label className="lg-check rg-check">
                                <input
                                    type="checkbox"
                                    checked={agree}
                                    onChange={e => setAgree(e.target.checked)}
                                />
                                <span>
                                    I agree to Interim's{' '}
                                    <Link to="/about" className="lg-link-tiny">Terms</Link>{' '}
                                    and{' '}
                                    <Link to="/about" className="lg-link-tiny">Privacy Policy</Link>.
                                </span>
                            </label>

                            {error && <div className="lg-error">{error}</div>}

                            <button
                                type="submit"
                                className="ix-btn ix-btn-primary ix-btn-lg lg-submit"
                                disabled={busy}
                            >
                                {busy ? 'Creating account…' : <>Create account <ArrowRight size={14} /></>}
                            </button>

                            <div className="lg-divider"><span>or sign up with</span></div>

                            <button
                                type="button"
                                className="lg-sso-btn"
                                onClick={() => setError('Google sign-up is coming soon.')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M21.6 12.23c0-.72-.06-1.4-.18-2.05H12v3.88h5.4a4.62 4.62 0 0 1-2 3.03v2.52h3.23c1.89-1.74 2.97-4.3 2.97-7.38Z" fill="#4285F4"/>
                                    <path d="M12 22c2.7 0 4.96-.9 6.63-2.43l-3.24-2.52c-.9.6-2.05.96-3.39.96-2.6 0-4.81-1.76-5.6-4.12H3.05v2.6A10 10 0 0 0 12 22Z" fill="#34A853"/>
                                    <path d="M6.4 13.89A6.01 6.01 0 0 1 6.07 12c0-.66.11-1.3.33-1.89V7.51H3.05A10 10 0 0 0 2 12c0 1.61.39 3.14 1.05 4.49l3.35-2.6Z" fill="#FBBC05"/>
                                    <path d="M12 5.98c1.47 0 2.79.5 3.83 1.5l2.87-2.87C16.95 2.99 14.7 2 12 2A10 10 0 0 0 3.05 7.51l3.35 2.6C7.19 7.74 9.4 5.98 12 5.98Z" fill="#EA4335"/>
                                </svg>
                                Sign up with Google
                            </button>
                        </form>

                        <div className="lg-foot">
                            <span>Already have an account?</span>
                            <Link to="/login" className="lg-link">
                                Log in <ArrowRight size={12} />
                            </Link>
                        </div>
                    </div>

                    <footer className="lg-legal mono">
                        © 2026 Interim &nbsp;·&nbsp;
                        <Link to="/about" style={{ color: 'inherit' }}>Terms</Link>
                        &nbsp;·&nbsp;
                        <Link to="/about" style={{ color: 'inherit' }}>Privacy</Link>
                    </footer>
                </div>

                {/* ── Right: aside panel ── */}
                <aside className="lg-aside">
                    <div className="lg-aside-bg" />
                    <div className="lg-aside-inner">

                        <h2 className="lg-aside-title">
                            Start working <span className="lg-aside-serif">today.</span>
                        </h2>
                        <p className="lg-aside-body">
                            Three steps and you're matched with single-day jobs near you.
                        </p>

                        <ol className="rg-steps">
                            <li className="rg-step">
                                <span className="rg-step-n">01</span>
                                <div>
                                    <div className="rg-step-title">Create your account</div>
                                    <div className="rg-step-sub">Name, email, password. That's it.</div>
                                </div>
                            </li>
                            <li className="rg-step">
                                <span className="rg-step-n">02</span>
                                <div>
                                    <div className="rg-step-title">Tell us about you</div>
                                    <div className="rg-step-sub">A short bio, what you do well, where you can work.</div>
                                </div>
                            </li>
                            <li className="rg-step">
                                <span className="rg-step-n">03</span>
                                <div>
                                    <div className="rg-step-title">Apply to your first job</div>
                                    <div className="rg-step-sub">Most workers get accepted within an hour.</div>
                                </div>
                            </li>
                        </ol>

                        <div className="lg-aside-stats">
                            <div>
                                <div className="lg-aside-stat-n">£780k</div>
                                <div>paid to workers this month</div>
                            </div>
                            <div className="lg-aside-stats-div" />
                            <div>
                                <div className="lg-aside-stat-n">4.9</div>
                                <div>average poster rating</div>
                            </div>
                        </div>

                    </div>
                </aside>
            </div>
        </div>
    );
};

export default RegistrationPage;
